import { type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import type { AuthUser } from '@/types/auth';

export type PendingSummary = {
  accumulated: number;
  minimum_payout_banned?: number;
};

function formatUSD(n: number): string {
  return '$' + n.toFixed(2).replace(/\.?0+$/, '');
}

export function getBanReason(user: AuthUser): string {
  // Fase 28: post-redemption second ban — they used their one-shot and
  // failed again. Final, no offer of another chance.
  if (user.has_used_redemption)
    return 'Has agotado tu redención. No hay más segundas oportunidades.';
  // Eligible for redemption: was healthy (passed test) and the streak
  // engine just banned them. The redemption block above will offer the
  // 24h window — copy here primes that handoff.
  if (user.test_passed_at && !user.has_used_redemption)
    return 'Has acumulado días sin alcanzar el mínimo. Tienes una última oportunidad para redimirte.';
  // Test phase failure — entered test, never reached healthy. The
  // test_started_at gate matters: a banned user who never started the
  // test (e.g. admin closed an unverified account) should not see this
  // misleading copy.
  if (user.test_started_at && !user.test_passed_at && !user.pre_banned_at)
    return 'No completaste la fase de prueba a tiempo.';
  // Legacy pre_banned_at survivor (auto-migrated to banned in fase 28).
  if (user.pre_banned_at)
    return 'Has acumulado 4 días dentro de una misma semana sin conseguir el objetivo mínimo.';
  return 'Tu cuenta ha sido cerrada por el administrador.';
}

const BTN_CLS =
  'w-full rounded-lg bg-brand-red px-base py-sm text-sm font-medium text-white transition-colors hover:bg-brand-coral';

export function PendingAmount({
  summary,
}: {
  summary: PendingSummary;
}): ReactNode {
  return (
    <div className="flex items-baseline justify-between">
      <span className="text-sm text-text-tertiary">Pendiente</span>
      <span className="font-mono text-lg font-semibold tabular-nums text-text-primary">
        {formatUSD(summary.accumulated)}
      </span>
    </div>
  );
}

export function WalletCta({ summary }: { summary: PendingSummary }): ReactNode {
  const navigate = useNavigate();
  const min = summary.minimum_payout_banned ?? 3;
  if (summary.accumulated < min) return null;
  return (
    <div className="space-y-sm">
      <p className="text-sm text-brand-red">
        Necesitas configurar tu billetera para recibir tu pago.
      </p>
      <button onClick={() => navigate('/wallet')} className={BTN_CLS}>
        Configurar billetera
      </button>
    </div>
  );
}
